import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, CardActions, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Alert, IconButton, Tooltip, Divider, LinearProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PaletteIcon from '@mui/icons-material/Palette';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  MY_CEREMONIES_QUERY,
  VENDORS_QUERY,
  DECORATION_THEMES_QUERY,
  MY_DECORATION_ASSIGNMENTS_QUERY,
  ASSIGN_DECORATION_MUTATION,
  UPDATE_DECORATION_ASSIGNMENT_MUTATION,
  DELETE_DECORATION_ASSIGNMENT_MUTATION,
} from '../graphql/queries';
import ContractedOfferingsPanel from '../components/ContractedOfferingsPanel';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const STATUS_COLOR = { planned: 'default', confirmed: 'info', in_progress: 'warning', completed: 'success' };
const STYLE_COLOR  = { traditional: '#b5451b', modern: '#1976d2', fusion: '#7b1fa2', royal: '#c9a227', rustic: '#5d4037', floral: '#e91e63' };
const EMPTY_FORM = { ceremonyId: '', themeId: '', customTheme: '', vendorId: '', budget: '', setupTime: '', notes: '' };

export default function DecorationPage() {
  const [tab, setTab]         = useState('assign');   // assign | themes
  const [dialog, setDialog]   = useState(false);
  const [editAssign, setEdit] = useState(null);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [filterStyle, setFilterStyle] = useState('');
  const [error, setError]     = useState('');

  const { data: ceremonyData } = useQuery(MY_CEREMONIES_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: vendorData }   = useQuery(VENDORS_QUERY, { variables: { vendorType: 'decorator' }, fetchPolicy: 'cache-and-network' });
  const { data: themeData }    = useQuery(DECORATION_THEMES_QUERY, { variables: filterStyle ? { style: filterStyle } : {}, fetchPolicy: 'cache-and-network' });
  const { data, loading, refetch } = useQuery(MY_DECORATION_ASSIGNMENTS_QUERY, { fetchPolicy: 'cache-and-network' });

  const ceremonies   = ceremonyData?.myCeremonies?.filter(c => c.status !== 'cancelled') || [];
  const vendors      = vendorData?.vendors || [];
  const themes       = themeData?.decorationThemes || [];
  const assignments  = data?.myDecorationAssignments || [];

  const [assign] = useMutation(ASSIGN_DECORATION_MUTATION, {
    onCompleted: () => { setDialog(false); setForm(EMPTY_FORM); refetch(); setError(''); },
    onError: e => setError(e.message),
  });
  const [updateAssign] = useMutation(UPDATE_DECORATION_ASSIGNMENT_MUTATION, {
    onCompleted: () => { setDialog(false); setEdit(null); setForm(EMPTY_FORM); refetch(); setError(''); },
    onError: e => setError(e.message),
  });
  const [deleteAssign] = useMutation(DELETE_DECORATION_ASSIGNMENT_MUTATION, { onCompleted: () => refetch() });

  const openCreate = () => { setEdit(null); setForm(EMPTY_FORM); setDialog(true); };
  const openEdit   = (a) => {
    setEdit(a);
    setForm({ ceremonyId: a.ceremonyId, themeId: a.themeId || '', customTheme: a.customTheme || '', vendorId: a.vendorId || '', budget: a.budget || '', setupTime: a.setupTime || '', notes: a.notes || '' });
    setDialog(true);
  };

  const handleSave = () => {
    const vars = { ...form, themeId: form.themeId || null, vendorId: form.vendorId || null, budget: form.budget ? Number(form.budget) : null };
    if (editAssign) updateAssign({ variables: { id: editAssign.id, ...vars } });
    else assign({ variables: vars });
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" spacing={1} alignItems="center">
          <PaletteIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>Decoration & Themes</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant={tab === 'assign' ? 'contained' : 'outlined'} onClick={() => setTab('assign')}>My Assignments</Button>
          <Button variant={tab === 'themes' ? 'contained' : 'outlined'} onClick={() => setTab('themes')}>Browse Themes</Button>
          {tab === 'assign' && <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} color="secondary">Assign Theme</Button>}
        </Stack>
      </Stack>

      <ContractedOfferingsPanel categories={['decoration']} title="Contracted decorators" />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Browse Themes Tab */}
      {tab === 'themes' && (
        <Box>
          <Stack direction="row" spacing={2} mb={3}>
            <TextField select label="Filter by Style" size="small" value={filterStyle} onChange={e => setFilterStyle(e.target.value)} sx={{ minWidth: 180 }}>
              <MenuItem value="">All Styles</MenuItem>
              {['traditional','modern','fusion','royal','rustic','floral'].map(s => (
                <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
              ))}
            </TextField>
          </Stack>
          {themes.length === 0 && (
            <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <PaletteIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">No decoration themes found. Ask your admin to add themes.</Typography>
            </Card>
          )}
          <Grid container spacing={2}>
            <Paged items={themes}>{rows => rows.map(theme => (
              <Grid item xs={12} sm={6} md={4} key={theme.id}>
                <Card variant="outlined" sx={{ borderTop: `4px solid ${STYLE_COLOR[theme.style] || '#888'}` }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography variant="h6" fontWeight={700}>{theme.name}</Typography>
                      <Chip label={theme.style} size="small" sx={{ textTransform: 'capitalize', bgcolor: STYLE_COLOR[theme.style] || '#888', color: 'white' }} />
                    </Stack>
                    {theme.description && <Typography variant="body2" color="text.secondary" mb={1}>{theme.description}</Typography>}
                    {theme.colorPalette && theme.colorPalette.length > 0 && (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" mb={1}>
                        {theme.colorPalette.map((color, i) => <Chip key={i} label={color} size="small" variant="outlined" />)}
                      </Stack>
                    )}
                    {theme.estimatedCost && (
                      <Typography variant="body2" color="success.main" fontWeight={600}>Est. Cost: ₹{Number(theme.estimatedCost).toLocaleString()}</Typography>
                    )}
                  </CardContent>
                  <CardActions>
                    <Button size="small" variant="outlined" onClick={() => { setTab('assign'); openCreate(); setForm(f => ({ ...f, themeId: theme.id })); }}>
                      Assign to Ceremony
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}</Paged>
          </Grid>
        </Box>
      )}

      {/* My Assignments Tab */}
      {tab === 'assign' && (
        <Box>
          {assignments.length === 0 && !loading && (
            <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <PaletteIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">No decoration assignments yet. Browse themes and assign one to a ceremony.</Typography>
            </Card>
          )}
          <Grid container spacing={2}>
            {assignments.map(a => {
              const ceremony = ceremonies.find(c => c.id === a.ceremonyId);
              return (
                <Grid item xs={12} md={6} key={a.id}>
                  <Card variant="outlined" sx={{ borderLeft: `5px solid ${STATUS_COLOR[a.status] === 'success' ? '#4caf50' : STATUS_COLOR[a.status] === 'warning' ? '#ff9800' : STATUS_COLOR[a.status] === 'info' ? '#2196f3' : '#bbb'}` }}>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography variant="subtitle1" fontWeight={700}>{a.ceremony?.name || ceremony?.name || 'Ceremony'}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {a.theme ? `Theme: ${a.theme.name} (${a.theme.style})` : a.customTheme ? `Custom: ${a.customTheme}` : 'No theme selected'}
                          </Typography>
                        </Box>
                        <Chip label={a.status} color={STATUS_COLOR[a.status] || 'default'} size="small" />
                      </Stack>
                      <Divider sx={{ my: 1 }} />
                      <Grid container spacing={1}>
                        {a.vendor && <Grid item xs={12}><Typography variant="caption" color="text.secondary">Decorator: <strong>{a.vendor.name}</strong></Typography></Grid>}
                        {a.budget && <Grid item xs={6}><Typography variant="caption" color="text.secondary">Budget: <strong>₹{Number(a.budget).toLocaleString()}</strong></Typography></Grid>}
                        {a.actualCost && <Grid item xs={6}><Typography variant="caption" color={a.actualCost > a.budget ? 'error.main' : 'success.main'}>Actual: <strong>₹{Number(a.actualCost).toLocaleString()}</strong></Typography></Grid>}
                        {a.setupTime && <Grid item xs={12}><Typography variant="caption" color="text.secondary">Setup Time: <strong>{a.setupTime}</strong></Typography></Grid>}
                        {a.theme?.colorPalette?.length > 0 && (
                          <Grid item xs={12}>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                              {a.theme.colorPalette.map((c, i) => <Chip key={i} label={c} size="small" variant="outlined" />)}
                            </Stack>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                    <CardActions>
                      <IconButton size="small" onClick={() => openEdit(a)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteAssign({ variables: { id: a.id } })}><DeleteIcon fontSize="small" /></IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Assign Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editAssign ? 'Update Decoration' : 'Assign Decoration Theme'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField select required label="Ceremony" fullWidth value={form.ceremonyId} onChange={e => setForm(f => ({ ...f, ceremonyId: e.target.value }))}>
              {ceremonies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
            <TextField select label="Decoration Theme" fullWidth value={form.themeId} onChange={e => setForm(f => ({ ...f, themeId: e.target.value }))}>
              <MenuItem value="">Custom / No theme</MenuItem>
              {themes.map(t => <MenuItem key={t.id} value={t.id}>{t.name} ({t.style})</MenuItem>)}
            </TextField>
            {!form.themeId && (
              <TextField label="Custom Theme Description" fullWidth value={form.customTheme} onChange={e => setForm(f => ({ ...f, customTheme: e.target.value }))} />
            )}
            <TextField select label="Decorator Vendor" fullWidth value={form.vendorId} onChange={e => setForm(f => ({ ...f, vendorId: e.target.value }))}>
              <MenuItem value="">TBD</MenuItem>
              {vendors.map(v => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
            </TextField>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="Budget (₹)" type="number" fullWidth value={form.budget} onChange={e => setForm(f => ({ ...f, budget: e.target.value }))} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Setup Time" fullWidth placeholder="e.g. 4 hours before" value={form.setupTime} onChange={e => setForm(f => ({ ...f, setupTime: e.target.value }))} />
              </Grid>
            </Grid>
            {editAssign && (
              <TextField select label="Status" fullWidth value={form.status || 'planned'} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                {['planned','confirmed','in_progress','completed'].map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s.replace('_',' ')}</MenuItem>)}
              </TextField>
            )}
            <TextField label="Notes" fullWidth multiline rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.ceremonyId}>{editAssign ? 'Save' : 'Assign'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
